import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { TextField, Button, Typography, Box, Alert, Link, FormControlLabel, Checkbox } from '@mui/material';
import AuthLayout from '../components/AuthLayout';
import EmailSuggestion from '../components/EmailSuggestion';
import { registration } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { LEGAL_DOCS } from '../legal/documents';
import { LEGAL_VERSION } from '../constants/company';

const MIN_PASSWORD_LENGTH = 8;

// Opens a legal document in a new tab so the half-filled form is not lost.
const LegalLink = ({ to, children }) => (
  <Link component={RouterLink} to={to} target="_blank" rel="noopener" sx={{ color: '#11BDB6', fontWeight: 600 }}
    onClick={(e) => e.stopPropagation()}>
    {children}
  </Link>
);

const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [suggestion, setSuggestion] = useState(''); // the address the server thinks was meant
  const [loading, setLoading] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  // 152-ФЗ: consent to personal data processing is a separate tick, not part of the offer
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [pdConsent, setPdConsent] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const partner = useSearchParams()[0].get('partner') === '1'; // came from a partner link or ad

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Пароль должен быть не короче ${MIN_PASSWORD_LENGTH} символов`);
      return;
    }
    if (password !== passwordConfirm) {
      setError('Пароли не совпадают');
      return;
    }
    if (!acceptTerms || !pdConsent) {
      setError('Чтобы зарегистрироваться, примите оферту и дайте согласие на обработку персональных данных');
      return;
    }

    setLoading(true);
    setEmailTaken(false);
    setSuggestion('');
    try {
      const { data } = await registration.register(email, password, { acceptTerms, pdConsent, legalVersion: LEGAL_VERSION });
      // signed in at once; the email is confirmed in the settings, where the partner application is filed too
      signIn(data);
      navigate(partner ? '/settings' : '/dashboard', { replace: true });
      return;
    } catch (err) {
      setEmailTaken(err.response?.data?.reason === 'email_taken');
      setSuggestion(err.response?.data?.suggestion || '');
      setError(err.response?.data?.error || 'Не удалось зарегистрироваться');
    }
    setLoading(false);
  };

  return (
    <AuthLayout subtitle="Регистрация">
      {partner && (
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: '12px' }}>
          Чтобы стать партнёром, сначала зарегистрируйтесь. Затем подтвердите почту и подайте заявку в разделе «Настройки».
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2.5,
            borderRadius: '12px',
            background: 'rgba(246,193,195,0.3)',
            border: '1px solid rgba(217,79,92,0.2)',
            color: '#D94F5C',
            '& .MuiAlert-icon': { color: '#D94F5C' },
          }}
        >
          {error}
          {emailTaken && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="contained" component={RouterLink} to={`/forgot-password?email=${encodeURIComponent(email)}`}
                sx={{ borderRadius: '10px', background: '#11BDB6' }}>
                Восстановить доступ
              </Button>
              <Button size="small" component={RouterLink} to="/login" sx={{ color: '#11BDB6' }}>Войти</Button>
            </Box>
          )}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          required
          fullWidth
          id="email"
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => { setEmail(e.target.value); setSuggestion(''); }}
        />
        <EmailSuggestion email={email} suggestion={suggestion} onApply={(v) => { setEmail(v); setSuggestion(''); }} />
        <TextField
          required
          fullWidth
          name="password"
          label="Пароль"
          type="password"
          id="password"
          autoComplete="new-password"
          helperText={`Не короче ${MIN_PASSWORD_LENGTH} символов`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <TextField
          required
          fullWidth
          name="passwordConfirm"
          label="Повторите пароль"
          type="password"
          id="passwordConfirm"
          autoComplete="new-password"
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', color: '#4B5563', pt: 1.1 } }}
            control={<Checkbox size="small" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} inputProps={{ 'aria-label': 'Принимаю условия оферты' }} />}
            label={<>Принимаю условия <LegalLink to={LEGAL_DOCS.offer.path}>публичной оферты</LegalLink></>}
          />
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', color: '#4B5563', pt: 1.1 } }}
            control={<Checkbox size="small" checked={pdConsent} onChange={(e) => setPdConsent(e.target.checked)} inputProps={{ 'aria-label': 'Даю согласие на обработку персональных данных' }} />}
            label={<>Даю <LegalLink to={LEGAL_DOCS.consent.path}>согласие на обработку персональных данных</LegalLink> в соответствии с <LegalLink to={LEGAL_DOCS.privacy.path}>политикой</LegalLink></>}
          />
        </Box>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || !acceptTerms || !pdConsent}
          sx={{
            mt: 1,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 700,
            borderRadius: '14px',
            background: loading
              ? 'rgba(25,198,192,0.5)'
              : 'linear-gradient(135deg, #19C6C0 0%, #11BDB6 100%)',
            boxShadow: loading ? 'none' : '0 6px 20px rgba(25,198,192,0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #11BDB6 0%, #0BABA5 100%)',
              boxShadow: '0 8px 24px rgba(25,198,192,0.5)',
            },
          }}
        >
          {loading ? 'Отправка...' : 'Зарегистрироваться'}
        </Button>
      </Box>

      <Typography sx={{ mt: 2.5, fontSize: '0.8125rem', color: '#6B7280', textAlign: 'center' }}>
        После регистрации вы сразу войдёте в сервис. Введите код из письма в разделе «Настройки»: это откроет тарифы и бесплатный доступ. Принимаются почты на mail.ru, yandex.ru и других российских сервисах.
      </Typography>

      <Typography sx={{ mt: 2, textAlign: 'center', fontSize: '0.875rem', color: '#6B7280' }}>
        Уже есть аккаунт?{' '}
        <Link component={RouterLink} to="/login" sx={{ fontWeight: 600, color: '#11BDB6' }}>
          Войти
        </Link>
      </Typography>
    </AuthLayout>
  );
};

export default RegisterPage;
